import { useState, useMemo, useEffect } from 'react';
import { useMemoizedFn } from 'ahooks';
import {
  fetchProjectsOfProjectsTab,
  queryDetailProjectsLiveOfInternal,
  queryDetailProjectsInDev,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import { PRECONDITION } from '../constants';
import { toFirstUpperCase } from '@gp/utils/common';

const SOURCES: IStudioOverallSource[] = [
  {
    text: 'Internal',
    value: 'internal',
  },
  {
    text: 'Intelligence',
    value: 'intelligence',
  },
];

export default function useLive(studioId: string, initialPage: any) {
  const [isLoading, setIsLoading] = useState<boolean>(true); // 两个筛选器数据的loading
  const [projectFilterLoading, setProjectFilterLoading] = useState<boolean>(true);
  const [isFilterChange, setIsFilterChange] = useState<boolean>(false); // 判断是否是筛选器变化
  const [projects, setProjects] = useState<IStudioOverallProject[]>([]);
  const [sources, setSources] = useState<IStudioOverallSource[]>([]);

  const [formModel, setFormModel] = useState<IStudioOverallProjectsDetailsLiveFilter>({
    projects: [],
    source: 'internal',
  });

  const [page, setPage] = useState(initialPage);

  const selectedProjects = useMemo<IStudioOverallProject[]>(() => {
    if (!Array.isArray(projects)) return [];
    const keys = Object.keys(projects?.[0] || {}).filter(
      (key) => !['text', 'value']?.includes(key),
    );
    const buildProject = (
      project: IStudioOverallProject,
      keys: string[],
    ): IStudioOverallProject => {
      return keys.reduce(
        (current, next) => ({ ...current, [next]: project[next] }),
        {} as IStudioOverallProject,
      );
    };

    return projects
      ?.filter((project) => formModel?.projects?.includes(project?.id))
      ?.map((project) => buildProject(project, keys));
  }, [formModel?.projects, projects]);

  const getProjects = useMemoizedFn(async (flag = false) => {
    let projects: IStudioOverallProject[] = [];
    try {
      const res: any = await fetchProjectsOfProjectsTab({
        studio_id: studioId || '',
        internal: flag ? true : undefined,
      });
      if (Array.isArray(res?.list)) {
        projects = res?.list?.map((project: any) => ({
          ...project,
          text: toFirstUpperCase(project?.name) || '',
          value: project?.id || '',
        }));
      }
    } catch (exception) {
      console.warn(exception);
      projects = [];
    }
    return projects;
  });

  const getSources = useMemoizedFn(() => {
    return Promise.resolve(SOURCES);
  });

  const init = useMemoizedFn(async () => {
    setIsLoading(true);
    setProjectFilterLoading(true);

    const internalParams = {
      select_id: [studioId],
      yoy: true,
      mom: true,
    };

    const [projectsPromiseRes, sourcesPromiseRes, internalRes, devRes] = await Promise.allSettled([
      getProjects(true),
      getSources(),
      queryDetailProjectsLiveOfInternal(internalParams),
      queryDetailProjectsInDev({ studio_id: studioId }),
    ]);
    const projects = projectsPromiseRes?.status === 'fulfilled' ? projectsPromiseRes?.value : [];
    const sources = sourcesPromiseRes?.status === 'fulfilled' ? sourcesPromiseRes?.value : [];
    const projectIds = projects?.map((project) => project?.value);
    const newFormModel = {
      ...formModel,
      projects: projectIds || [],
    };
    setProjects(projects || []);
    setSources(sources || []);
    setFormModel(newFormModel);

    const hasLive = internalRes && internalRes?.value?.game_detail?.length !== 0;
    const hasDev = devRes && devRes?.value?.game_detail?.length !== 0;
    if (hasLive && !hasDev) {
      setPage('live');
    } else if (!hasLive && hasDev) {
      setPage('development');
    }

    setIsLoading(false);
    setProjectFilterLoading(false);
  });

  const handleFilterChange = useMemoizedFn((payload, filterName) => {
    if (filterName == 'source') setProjectFilterLoading(true);
    const newFormModel = {
      ...formModel,
      ...payload,
    };
    setFormModel(newFormModel);
    setIsFilterChange(true);
  });

  useEffect(() => {
    init();
  }, [studioId]);

  const projectsFilterListChange = async (source: string) => {
    setProjectFilterLoading(true);
    const res = await getProjects(source == PRECONDITION);
    const projectIds = res?.map((project) => project?.value);
    const newFormModel = {
      ...formModel,
      projects: projectIds || null,
    };
    setProjects(res || null);
    setFormModel(newFormModel);
    setIsLoading(false);
    setProjectFilterLoading(false);
  };

  // source筛选器变化，project筛选器数据发生变化
  useEffect(() => {
    if (isFilterChange) {
      projectsFilterListChange(formModel?.source);
      setIsFilterChange(false);
    }
  }, [formModel?.source]);

  return {
    isLoading,
    projectFilterLoading,
    projects,
    sources,
    formModel,
    selectedProjects,
    handleFilterChange,
    page,
    setPage,
  };
}
